import { Button } from "@core/button";
import { X } from "lucide-react";
import { ReactNode } from "react";
import { Text } from "@core/text";

interface ModalProps {
  id?: string;
  show: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  hideCloseButton?: boolean;
  outerClassName?: string;
  bare?: boolean;
  actions?: {
    label: string;
    onClick: () => void;
    variant?: "primary" | "secondary" | "danger";
    loading?: boolean;
    disabled?: boolean;
  }[];
}

const Modal = ({
  id,
  show,
  onClose,
  title,
  children,
  hideCloseButton = false,
  actions,
  outerClassName = "max-w-lg sm:max-w-lg md:max-w-xl",
  bare = false,
}: ModalProps) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 w-full h-full min-h-screen z-[9999]">
      <div className="fixed inset-0 w-full h-full min-h-screen bg-black/50 backdrop-blur-sm" />

      <div className="relative w-full h-full flex items-center justify-center">
        <div
          className={`${id} relative w-full transform transition-all sm:my-8 ${
            bare
              ? outerClassName
              : `overflow-hidden rounded-lg bg-white dark:bg-dark-500 p-6 text-left shadow-xl ${outerClassName}`
          }`}
        >
          {!bare && (
            <>
              <div className="absolute right-4 top-4">
                {!hideCloseButton && (
                  <Button
                    onClick={onClose}
                    variant="ghost"
                    size="icon"
                    rounded="full"
                    textColor="text-gray-700 dark:text-gray-300"
                  >
                    <X size={20} className="text-gray-500 dark:text-gray-400" />
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between mb-4">
                <Text variant="h3" className="font-bold text-xl">
                  {title}
                </Text>
              </div>
            </>
          )}

          <div className={bare ? "" : "mt-2"}>{children}</div>

          {!bare && actions && actions.length > 0 && (
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 justify-end">
              {actions.map((action, index) => (
                <Button
                  key={index}
                  onClick={action.onClick}
                  rounded="md"
                  size="md"
                  disabled={action.disabled || action.loading}
                  loading={action.loading}
                  textColor={
                    action.variant === "primary"
                      ? "text-white"
                      : action.variant === "danger"
                        ? "text-white"
                        : "text-gray-800 dark:text-gray-100"
                  }
                  bgColor={
                    action.variant === "primary"
                      ? "bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                      : action.variant === "danger"
                        ? action.loading || action.disabled
                          ? "bg-rose-100 hover:bg-rose-100 dark:bg-rose-700 dark:hover:bg-rose-700"
                          : "bg-rose-500 hover:bg-rose-600 dark:bg-rose-600 dark:hover:bg-rose-500"
                        : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                  }
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
